// Production runs and the cost model (ERP-PLAN §5.13), spread into `mutations` (shared/mutations.ts): runs and their
// plan, linking POs, costs and invoices, the tooling ledger, the timeline, attachments' details, planned freight and
// duty rates, the opening position of a run recorded after it started, back-dating a PO, and the explicit Shopify cost
// push. Rules and implementations: server/src/mutations/runs.ts. Uploads and downloads: server/src/attachments.ts.

import { z } from "zod";
import { create, custom, remove, update } from "./mutation-kit.ts";
import { PAID_VIA } from "./pay-accounts.ts";
import {
  attachmentFields,
  dutyRateFields,
  eventFields,
  freightRateFields,
  openingInput,
  productionCostFields,
  productionRunFields,
  RUN_STATUSES,
  RUN_SUPPLIES,
  runLineFields,
  toolingFields,
} from "./runs.ts";

const isoDate = z.iso.date("Use a date like 2026-12-31");
const when = z.iso.datetime({ offset: true }).optional();

export const runMutations = {
  /**
   * A run in a project (the browser makes the id), with its plan's lines. Returns { id, number }. `historical`: a past
   * run (migration 057), recorded after the fact: closed from the start on `finished_on`, its lines the units it built.
   */
  "production_run.create": custom(
    productionRunFields.partial().extend({
      id: z.uuid(),
      project_id: z.uuid(),
      historical: z.boolean().optional(),
      lines: z
        .array(z.object({ id: z.uuid().optional(), item_id: z.uuid(), qty: z.number().min(0).max(10_000_000), bom_id: z.uuid().nullable().optional() }))
        .max(500)
        .default([]),
    }),
  ),
  "production_run.update": update("production_run", productionRunFields),
  /** Only a run still planning, with nothing linked or recorded on it. */
  "production_run.remove": remove("production_run"),
  /**
   * Moves a run along (shared/runs.ts NEXT_RUN_STATUSES). Leaving planning freezes the plan's prices and rates
   * (plan_basis); closing records closed_at. `at`: when it really happened (back-dating). Writes a timeline entry.
   */
  "production_run.set_status": custom(z.object({ id: z.uuid(), status: z.enum(RUN_STATUSES), at: when, note: z.string().trim().max(2000).optional() })),
  /**
   * Who supplies a part for this run (migration 057): one of RUN_SUPPLIES, or null to go back to what the BOM says. The
   * run's make orders that use it take it into their parts (refused once the CM has reported units built on one).
   */
  "production_run.set_supply": custom(z.object({ id: z.uuid(), item_id: z.uuid(), supply: z.enum(RUN_SUPPLIES).nullable() })),
  /** Freezes the plan again with today's prices and rates (until the run is closed). */
  "production_run.replan": custom(z.object({ id: z.uuid() })),
  /** The BOM revision a run builds a product's variants from (null: the current one). */
  "production_run.set_bom": custom(z.object({ id: z.uuid(), item_ids: z.array(z.uuid()).min(1).max(500), bom_id: z.uuid().nullable() })),
  "production_run_line.create": create("production_run_line", runLineFields.partial().required({ run_id: true, item_id: true })),
  "production_run_line.update": update("production_run_line", runLineFields.omit({ run_id: true, item_id: true })),
  "production_run_line.remove": remove("production_run_line"),
  /**
   * What had already happened before the run was recorded here, as of a date (shared/runs.ts openingInput): output
   * reports dated then, an opening count of the parts at the CM site, received units on the make PO lines. Once.
   */
  "production_run.record_opening": custom(openingInput.extend({ id: z.uuid() })),

  /** Puts a PO on a run (run_id null takes it off). A PO is on one run at most: one on another moves only with `move`. */
  "po.link_run": custom(z.object({ po_id: z.uuid(), run_id: z.uuid().nullable(), move: z.boolean().optional() })),
  /**
   * Paper POs --- a PO of a past run, recorded from paper (Mitch, 2026-11-03): a make PO for its builds (from the run's
   * BOM revisions, its make orders taking who supplies each part as the run says), or a purchase or service PO for what
   * was bought for it. Recorded as `historical` and received on `dated_on`, its lines all delivered: never sent or
   * emailed, never incoming in Shopify, never open for planning, receiving or consignment. On the run from the start,
   * with one timeline entry on its date. Returns { id, number }; the same id twice is a no-op. Without `run_id`, a
   * purchase or service PO from before HHL Ops that's already in (Mitch, 2026-11-04: One Key's PO-DO-1048, "Enter the
   * po so stock and cost are right"): on the books with its prices and payment, its goods counted where they are already
   * (Shopify's counts, the ledger), so nothing is received twice.
   */
  "po.record_paper": custom(
    z.object({
      id: z.uuid(),
      /** The past run it was for; left out: an order from before HHL Ops on no run (purchase or service only). */
      run_id: z.uuid().optional(),
      vendor_id: z.uuid(),
      kind: z.enum(["make", "purchase", "service"]),
      vendor_ref: z.string().trim().max(200).default(""),
      dated_on: isoDate,
      /** When its goods arrived (migration 078), for how long it took; left out: not known. */
      received_on: isoDate.nullable().optional(),
      notes: z.string().trim().max(5000).default(""),
      lines: z
        .array(
          z.object({
            id: z.uuid(),
            item_id: z.uuid(),
            qty: z.number().positive("Each line needs its quantity").max(10_000_000),
            /** Left out: what HHL Ops knows (a build's BOM charges, as Create make PO; a part's price list). */
            unit_price: z.number().min(0, "A price is 0 or more").max(10_000_000).nullable().optional(),
          }),
        )
        .min(1, "Add the lines from the paper")
        .max(500),
      /**
       * How it was paid (Mitch, 2026-11-03: "ensure that payments are accounted for and linked"), with the PO in one go:
       * a Wise history transfer linked to it (its bill and paid payment made by the link), or paid outside (a bill for the
       * PO and a payment saying which card or account and where). Needs Can pay. Left out: not recorded here.
       */
      paid: z
        .discriminatedUnion("how", [
          z.object({ how: z.literal("wise"), transfer_id: z.uuid() }),
          z.object({
            how: z.literal("outside"),
            method: z.enum(["card", "wire", "other"]),
            pay_account_id: z.uuid().nullable().optional(),
            paid_via: z.enum(["", ...PAID_VIA]).optional(),
            paid_on: isoDate,
            reference: z.string().trim().max(200).default(""),
          }),
        ])
        .optional(),
    }),
  ),
  /**
   * The dates on paper for a PO recorded after the fact: its own date, when it was sent (ordered), acknowledged and
   * received (migration 078: for how long it took). Never sends it or starts a revision; created_at, sent_at and
   * acknowledged_at stay when it was recorded here.
   */
  "po.set_history": custom(
    z.object({
      id: z.uuid(),
      dated_on: isoDate.nullable().optional(),
      sent_on: isoDate.nullable().optional(),
      acknowledged_on: isoDate.nullable().optional(),
      received_on: isoDate.nullable().optional(),
    }),
  ),
  /**
   * How long a PO took (ordered → received, poDates()) as its vendor's lead time for its items: each line's link to the
   * vendor (a make PO's: the CM's link for what it built), made if there's none. Returns { updated, made }.
   */
  "po.use_lead_time": custom(z.object({ po_id: z.uuid(), days: z.number().int().min(0).max(1000) })),
  /**
   * Marks PO lines on a run as run goods (bought by the run but not in its builds: cables for backers): in the run's
   * spend, never in the cost per unit. HHL's own bookkeeping: no new revision, nothing the vendor sees.
   */
  "po_line.set_run_goods": custom(z.object({ ids: z.array(z.uuid()).min(1).max(500), run_goods: z.boolean() })),

  /**
   * A cost of a run that isn't on one of its POs already. Refused when it repeats something the run already counts
   * (a PO's lines, a landed-cost charge, another entry) unless confirm_duplicate. A non-USD amount needs usd_rate.
   * Returns { id, already? }: the same id twice is a no-op (the MCP's request_id).
   */
  "production_cost.create": custom(
    productionCostFields
      .partial()
      .required({ run_id: true, category: true })
      .extend({ id: z.uuid(), confirm_duplicate: z.boolean().optional(), external_ref: z.string().trim().max(200).optional() }),
  ),
  "production_cost.update": update("production_cost", productionCostFields.omit({ run_id: true })),
  "production_cost.remove": remove("production_cost"),

  "tooling_item.create": create("tooling_item", toolingFields.partial().required({ project_id: true, name: true })),
  "tooling_item.update": update("tooling_item", toolingFields.omit({ project_id: true })),
  "tooling_item.remove": remove("tooling_item"),

  /** A note, photos, a QC report, a golden-sample note or a message worth keeping (files: attachments). */
  "production_event.create": create("production_event", eventFields.partial().required({ run_id: true, kind: true })),
  "production_event.update": update("production_event", eventFields.omit({ run_id: true })),
  "production_event.remove": remove("production_event"),

  "attachment.update": update("attachment", attachmentFields),
  "attachment.remove": remove("attachment"),

  "freight_rate.create": create("freight_rate", freightRateFields.partial().required({ project_id: true })),
  "freight_rate.update": update("freight_rate", freightRateFields.omit({ project_id: true, vendor_id: true })),
  "freight_rate.remove": remove("freight_rate"),
  "duty_rate.create": create("duty_rate", dutyRateFields.partial()),
  "duty_rate.update": update("duty_rate", dutyRateFields),
  "duty_rate.remove": remove("duty_rate"),

  /**
   * Sets Shopify unit costs to the landed cost the preview showed (through the catalog editor's shopify.edit_variants),
   * never by itself. Refused if a cost no longer matches what shared/costs.ts works out now. Returns { changed }.
   */
  "costs.update_shopify_costs": custom(
    z.object({
      project_id: z.uuid(),
      costs: z
        .array(z.object({ item_id: z.uuid(), std_cost: z.number().nonnegative("A cost is 0 or more") }))
        .min(1, "Pick at least one variant")
        .max(500),
    }),
  ),
  /**
   * Sets Shopify unit costs to a run's landed cost per unit (shared/costs.ts runLandedRows(): its variants built, every
   * cost known), as its Landed cost tab showed them. Refused if a cost no longer matches. Returns { changed }.
   */
  "costs.set_shopify_from_run": custom(
    z.object({
      run_id: z.uuid(),
      costs: z
        .array(z.object({ item_id: z.uuid(), std_cost: z.number().nonnegative("A cost is 0 or more") }))
        .min(1, "Pick at least one variant")
        .max(500),
    }),
  ),
} as const;
